"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.gameBridge = exports.createGameBridge = void 0;
const displayError_1 = require("./displayError");
const uuid_1 = require("uuid");
// Use uuid to generate unique callback names, since they are set on the window object
// Remove hyphens from resulting guids, so the return value is a valid function name
const createCallbackName = () => `callback_${(0, uuid_1.v4)().replaceAll('-', '')}`;
const createGameBridge = (showErrors = false, api = window.jsBridge) => {
    const handleError = (error, message) => {
        var _a;
        if (showErrors) {
            (0, displayError_1.default)([message, (_a = error.message) !== null && _a !== void 0 ? _a : error].filter(Boolean).join(' - '));
        }
        console.error(error);
    };
    const post = (action, payload) => {
        console.log(`Action: ${action} Payload: ${payload}`);
        try {
            api.post(action, payload);
        }
        catch (error) {
            handleError(error, `post(${action}, ${payload})`);
        }
    };
    const subscribe = (channel, id, callback) => {
        console.log(`Subscribing to channel: ${channel} id: ${id} callback: ${callback}`);
        const callbackName = createCallbackName();
        window[callbackName] = callback;
        try {
            api.subscribe(channel, id, callbackName);
        }
        catch (error) {
            handleError(error, `subscribe(${channel}, ${id})`);
            window[callbackName] = null;
        }
    };
    const query = (channel, id, callback) => {
        console.log(`Querying channel: ${channel} id: ${id} callback: ${callback}`);
        const callbackName = createCallbackName();
        window[callbackName] = (arg) => {
            callback(arg);
            window[callbackName] = null;
        };
        try {
            api.query(channel, id, callbackName);
        }
        catch (error) {
            handleError(error, `query(${channel}, ${id})`);
            window[callbackName] = null;
        }
    };
    const queryAsync = (channel, id) => new Promise((resolve, reject) => {
        console.log(`Querying channel: ${channel} id: ${id}`);
        const callbackName = createCallbackName();
        window[callbackName] = (arg) => {
            resolve(arg);
            window[callbackName] = null;
        };
        try {
            api.query(channel, id, callbackName);
        }
        catch (error) {
            handleError(error, `query(${channel}, ${id})`);
            window[callbackName] = null;
            reject(error);
        }
    });
    // The loc channel will not have data until it's done downloading and parsing the webview_localization.json (or locale override)
    // Use this promise to delay any localize calls from resolving until the data provider is ready
    let _locReadyPromise;
    const getLocReadyPromise = function () {
        return (_locReadyPromise !== null && _locReadyPromise !== void 0 ? _locReadyPromise : (_locReadyPromise = new Promise((resolve) => {
            // See LOC_READY_KEY from DataProvider_Localization.h
            subscribe('loc', '_LOC_READY_', (ready) => {
                // See LOC_READY_VALUE from DataProvider_Localization.h
                if (ready === 'READY') {
                    resolve();
                }
            });
        })));
    };
    const localize = (key, method, callback) => {
        if (method === 'sync') {
            try {
                return query('loc', key, callback);
            }
            catch (error) {
                handleError(error, `localize(${key})`);
                return '';
            }
        }
        try {
            // loc channel takes arbitrary keys, need to make query and queryAsync take any string and decorate to enforce types for specific channels
            return getLocReadyPromise().then(() => queryAsync('loc', key));
        }
        catch (error) {
            handleError(error, `localize(${key})`);
            return Promise.reject('');
        }
    };
    const dlogEvent = (data) => {
        try {
            post('dlogEvent', JSON.stringify(data));
        }
        catch (error) {
            handleError(error, `dlogEvent()`);
        }
    };
    return {
        post,
        localize,
        subscribe,
        query,
        queryAsync,
        dlogEvent,
    };
};
exports.createGameBridge = createGameBridge;
exports.gameBridge = (0, exports.createGameBridge)(false);
